import React from 'react';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ModeEnum, TvDashboardSettingsDTO, TvFieldSettingDTO } from '@api/ringil-api.v2.tsx';

export const EMPTY_TV_DASHBOARD_SETTINGS: TvDashboardSettingsDTO = {
  columnsCount: 3,
  reservationName: { mode: ModeEnum.FULL, bold: false },
  orderIds: { mode: ModeEnum.FULL, bold: false },
  time: { mode: ModeEnum.FULL, bold: false },
  carrier: { mode: ModeEnum.FULL, bold: false },
  carPlate: { mode: ModeEnum.FULL, bold: false },
  showUnfinished: false,
};

export type TvFieldKey = 'reservationName' | 'orderIds' | 'time' | 'carrier' | 'carPlate';

interface ComponentProps {
  value: TvDashboardSettingsDTO;
  maxColumns: number;
  onChange: (value: TvDashboardSettingsDTO) => void;
  readOnly?: boolean;
  dataTestId?: string;
}

export const useTvFieldLabels = () => {
  const { tr } = useTranslationLgs();
  // Same order as the TV renders them: name with orders, then time, then carrier with plate.
  return [
    { key: 'reservationName' as TvFieldKey, label: tr('LocalityDetail.tvShowReservationName', 'Reservation name') },
    { key: 'orderIds' as TvFieldKey, label: tr('LocalityDetail.tvShowOrders', 'Order IDs') },
    { key: 'time' as TvFieldKey, label: tr('LocalityDetail.tvShowTime', 'Reservation date') },
    { key: 'carrier' as TvFieldKey, label: tr('LocalityDetail.tvShowCarrier', 'Carrier') },
    { key: 'carPlate' as TvFieldKey, label: tr('LocalityDetail.tvShowCarPlate', 'Car plate') },
  ];
};

const TvDashboardSettingsEditor: React.FC<ComponentProps> = ({ value, maxColumns, onChange, readOnly, dataTestId }) => {
  const { tr } = useTranslationLgs();
  const fields = useTvFieldLabels();
  const settings = value ?? EMPTY_TV_DASHBOARD_SETTINGS;

  const modes = [
    { code: ModeEnum.HIDDEN, label: tr('LocalityDetail.tvModeHidden', "Don't show") },
    { code: ModeEnum.FULL, label: tr('LocalityDetail.tvModeFull', 'Show whole') },
    { code: ModeEnum.TRUNCATED, label: tr('LocalityDetail.tvModeTruncated', 'Shorten') },
  ];

  const setField = (key: TvFieldKey, patch: Partial<TvFieldSettingDTO>) =>
    onChange({ ...settings, [key]: { ...settings[key], ...patch } });

  return (
    <div className="flex flex-col gap-3">
      <Field label={tr('LocalityDetail.tvColumnsCount', 'Reservations shown per ramp')} readOnly={readOnly}>
        <Dropdown
          value={settings.columnsCount}
          options={Array.from({ length: maxColumns }, (_, i) => ({ code: i + 1, label: `${i + 1}` }))}
          optionValue={'code'}
          optionLabel={'label'}
          onChange={e => onChange({ ...settings, columnsCount: e.value })}
          data-testid={dataTestId ? `${dataTestId}-columnsCount` : undefined}
        />
      </Field>

      <RingilInputSwitch
        label={tr('LocalityDetail.tvShowUnfinished', 'Unfinished')}
        value={settings.showUnfinished}
        onChange={v => onChange({ ...settings, showUnfinished: v })}
        disabled={readOnly}
        dataTestId={dataTestId ? `${dataTestId}-showUnfinished` : undefined}
      />

      {fields.map(f => (
        <Field
          key={f.key}
          label={f.label}
          readOnly={readOnly}
          actionButtons={
            <RingilInputSwitch
              label={tr('LocalityDetail.tvBold', 'Bold')}
              value={settings[f.key].bold}
              onChange={v => setField(f.key, { bold: v })}
              disabled={readOnly || settings[f.key].mode === ModeEnum.HIDDEN}
              dataTestId={dataTestId ? `${dataTestId}-${f.key}-bold` : undefined}
            />
          }
        >
          <InputSelect
            model={modes}
            selectedItemCode={settings[f.key].mode}
            onSelect={code => setField(f.key, { mode: code as ModeEnum })}
            disabled={readOnly}
            showCheckIcon={false}
            dataTestIdPrefix={dataTestId ? `${dataTestId}-${f.key}` : undefined}
          />
        </Field>
      ))}

    </div>
  );
};

export default TvDashboardSettingsEditor;
